import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type { 
  Sale, 
  Client, 
  Expense, 
  InventoryBatch, 
  StockMovement, 
  CallLog, 
  User,
  Store,
  StoreRequest,
  InventoryTransfer,
  StoreAudit,
  AuditLogEntry,
  AuditCollection,
  AuditAction,
  UserRole,
  CustomerOrder,
  OrderStatus,
  OnionType,
  OnionSize,
  PaymentMethod,
  OrderPaymentMethod,
  DeliveryExpense,
  QualityClaim,
  Membership,
  SupplyPlan,
  OrderItem
} from '@/types';
import { SUPPLY_FREQUENCY_DAYS } from '@/types';
import { calculateClientMetrics } from '@/utils/clientAnalytics';

// Helper to convert Firestore timestamp to Date
const timestampToDate = (timestamp: any): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Generic CRUD operations
export const createDocument = async <T>(
  collectionName: string,
  id: string,
  data: Omit<T, 'id'>
): Promise<void> => {
  const docRef = doc(db, collectionName, id);
  await setDoc(docRef, {
    ...data,
    createdAt: Timestamp.now(),
  });
};

export const updateDocument = async <T>(
  collectionName: string,
  id: string,
  data: Partial<T>
): Promise<void> => {
  const docRef = doc(db, collectionName, id);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
};

export const deleteDocument = async (
  collectionName: string,
  id: string
): Promise<void> => {
  const docRef = doc(db, collectionName, id);
  await deleteDoc(docRef);
};

export const getDocument = async <T>(
  collectionName: string,
  id: string
): Promise<T | null> => {
  const docRef = doc(db, collectionName, id);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as T;
};

export const queryDocuments = async <T>(
  collectionName: string,
  constraints: QueryConstraint[] = []
): Promise<T[]> => {
  const collectionRef = collection(db, collectionName);
  const q = query(collectionRef, ...constraints);
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as T[];
};

// Sales operations
export const createSale = async (sale: Omit<Sale, 'id'>): Promise<string> => {
  const salesRef = collection(db, 'sales');
  const docRef = doc(salesRef);
  await setDoc(docRef, {
    ...sale,
    createdAt: Timestamp.now(),
    saleDate: Timestamp.fromDate(new Date(sale.saleDate)),
  });
  return docRef.id;
};

export const getSalesByClient = async (clientId: string): Promise<Sale[]> => {
  const sales = await queryDocuments<Sale>('sales', [
    where('clientId', '==', clientId),
    orderBy('saleDate', 'desc'),
  ]);
  
  return sales.map(sale => ({
    ...sale,
    saleDate: timestampToDate(sale.saleDate),
    createdAt: timestampToDate(sale.createdAt),
  })) as Sale[];
};

export const getSalesByDateRange = async (start: Date, end: Date): Promise<Sale[]> => {
  const sales = await queryDocuments<Sale>('sales', [
    where('saleDate', '>=', Timestamp.fromDate(start)),
    where('saleDate', '<=', Timestamp.fromDate(end)),
    orderBy('saleDate', 'desc'),
  ]);
  
  return sales.map(sale => ({
    ...sale,
    saleDate: timestampToDate(sale.saleDate),
    createdAt: timestampToDate(sale.createdAt),
  })) as Sale[];
};

export const getAllSales = async (): Promise<Sale[]> => {
  const sales = await queryDocuments<any>('sales', [
    orderBy('saleDate', 'desc'),
  ]);
  
  // Convert Firestore Timestamps to Dates
  return sales.map(sale => ({
    ...sale,
    saleDate: timestampToDate(sale.saleDate),
    createdAt: timestampToDate(sale.createdAt),
  })) as Sale[];
};

// Client operations
export const createOrUpdateClient = async (client: Client): Promise<void> => {
  const clientRef = doc(db, 'clients', client.id);
  
  // Prepare data object, excluding date fields from spread
  const { lastPurchaseDate, firstPurchaseDate, secondLastPurchaseDate, predictedNextPurchase, createdAt, lastUpdated, ...clientData } = client;
  
  const dataToSave: any = {
    ...clientData,
    lastUpdated: Timestamp.now(),
    lastPurchaseDate: Timestamp.fromDate(new Date(lastPurchaseDate)),
    firstPurchaseDate: Timestamp.fromDate(new Date(firstPurchaseDate)),
    createdAt: Timestamp.fromDate(new Date(createdAt)),
  };
  
  // Only add optional date fields if they exist
  if (secondLastPurchaseDate) {
    dataToSave.secondLastPurchaseDate = Timestamp.fromDate(new Date(secondLastPurchaseDate));
  }
  if (predictedNextPurchase) {
    dataToSave.predictedNextPurchase = Timestamp.fromDate(new Date(predictedNextPurchase));
  }
  
  // Remove all undefined values from the data object (Firestore doesn't allow undefined)
  Object.keys(dataToSave).forEach(key => {
    if (dataToSave[key] === undefined) {
      delete dataToSave[key];
    }
  });
  
  await setDoc(clientRef, dataToSave, { merge: true });
};

export const getClient = async (clientId: string): Promise<Client | null> => {
  const client = await getDocument<Client>('clients', clientId);
  if (!client) return null;
  
  // Convert timestamps to dates
  return {
    ...client,
    lastPurchaseDate: timestampToDate(client.lastPurchaseDate),
    firstPurchaseDate: timestampToDate(client.firstPurchaseDate),
    secondLastPurchaseDate: client.secondLastPurchaseDate 
      ? timestampToDate(client.secondLastPurchaseDate)
      : undefined,
    predictedNextPurchase: client.predictedNextPurchase
      ? timestampToDate(client.predictedNextPurchase)
      : undefined,
    lastUpdated: timestampToDate(client.lastUpdated),
    createdAt: timestampToDate(client.createdAt),
  };
};

/**
 * Convert a delivered CustomerOrder into a Sale and sync the client profile.
 * Idempotent: returns the existing saleId if the order was already converted.
 * Mirrors the manual SaleFormModal flow (createSale -> client metrics resync)
 * so order-derived revenue feeds every Sales/CFO dashboard and Sal prediction.
 */
export const createSaleFromOrder = async (
  order: CustomerOrder,
  userId: string,
): Promise<string> => {
  if (order.saleId) return order.saleId; // already converted

  const clientId = (order.customerPhone || '').replace(/\D/g, '') || order.id;

  // Aggregate the order's line items into one Sale (v1: one Sale per order).
  const items = order.items || [];
  const types = Array.from(new Set(items.map(i => i.onionType)));
  const sizes = Array.from(new Set(items.map(i => i.onionSize)));
  const onionType: OnionType = types.length === 1 ? types[0] : 'Mixed';
  const onionSize: OnionSize = sizes.length === 1 ? sizes[0] : 'Mixed';

  const quantityKg = order.totalWeightKg || items.reduce((s, i) => s + (i.quantityKg || 0), 0);
  // Product revenue only — delivery fee is tracked separately (delivery earnings).
  const productValue = items.reduce((s, i) => s + (i.subtotal || 0), 0)
    || order.estimatedTotal
    || quantityKg * (order.pricePerKgSnapshot || 0);
  const pricePerKg = quantityKg > 0 ? Math.round(productValue / quantityKg) : (order.pricePerKgSnapshot || 0);

  const isPaid = order.paymentStatus === 'paid';
  const paymentMethod: PaymentMethod = order.paymentMethod === 'pay_before' ? 'M-Pesa' : 'Cash';

  const sale: Omit<Sale, 'id'> = {
    clientId,
    clientName: order.customerName,
    clientPhone: order.customerPhone,
    clientLocation: [order.deliveryLocation, order.estate].filter(Boolean).join(' · '),
    onionType,
    onionSize,
    pricePerKg,
    quantityKg,
    totalValue: productValue,
    paymentMethod,
    paymentStatus: isPaid ? 'Paid' : 'Credit',
    amountPaid: isPaid ? productValue : 0,
    ...(isPaid ? {} : { amountOwed: productValue }),
    saleDate: order.deliveredAt || new Date(),
    notes: `Auto-created from online order ${order.trackingCode}`,
    linkedOrderId: order.id,
    createdAt: new Date(),
    createdBy: userId,
  };

  const saleId = await createSale(sale);

  // Resync the client profile from the full sales history (same path as manual sales).
  try {
    const existing = await getClient(clientId);
    const clientSales = await getSalesByClient(clientId);
    const metrics = calculateClientMetrics(clientSales);
    await createOrUpdateClient({
      id: clientId,
      name: order.customerName,
      phone: order.customerPhone,
      location: sale.clientLocation,
      ...metrics,
      createdAt: existing?.createdAt || new Date(),
    } as Client);
  } catch (e) {
    console.error('Client profile resync failed for order', order.trackingCode, e);
    // Non-fatal: the Sale is already recorded.
  }

  return saleId;
};

// ── Delivery expenses (actual cost per delivery) ──────────────────────────────
export const createDeliveryExpense = async (
  e: Omit<DeliveryExpense, 'id' | 'createdAt'>,
): Promise<string> => {
  const ref = doc(collection(db, 'deliveryExpenses'));
  await setDoc(ref, {
    ...e,
    date: Timestamp.fromDate(new Date(e.date)),
    createdAt: Timestamp.now(),
  });
  return ref.id;
};

export const getAllDeliveryExpenses = async (): Promise<DeliveryExpense[]> => {
  const rows = await queryDocuments<any>('deliveryExpenses', [orderBy('date', 'desc')]);
  return rows.map(r => ({
    ...r,
    date: timestampToDate(r.date),
    createdAt: timestampToDate(r.createdAt),
  })) as DeliveryExpense[];
};

// ── Payment intents (IntaSend M-Pesa STK, org-policy-proof) ───────────────────
// Client writes the intent; a Firestore trigger fires the STK push and a poller
// confirms via IntaSend's status API — no public HTTP endpoint involved.
export const createPaymentIntent = async (
  kind: 'order' | 'membership',
  ref: { trackingCode?: string; membershipKey?: string },
): Promise<string> => {
  const docRef = doc(collection(db, 'paymentIntents'));
  await setDoc(docRef, {
    kind,
    ...stripUndefined(ref as Record<string, unknown>),
    status: 'pending',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return docRef.id;
};

// ── B2B supply plans (standing orders) ────────────────────────────────────────
const mapPlan = (id: string, d: any): SupplyPlan => ({
  id,
  ...d,
  nextDeliveryDate: d.nextDeliveryDate ? timestampToDate(d.nextDeliveryDate) : undefined,
  createdAt: timestampToDate(d.createdAt),
  updatedAt: timestampToDate(d.updatedAt),
});

/** B2B request — creates a pending plan (rules forbid self-activation). */
export const createSupplyPlanRequest = async (
  data: Omit<SupplyPlan, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'nextDeliveryDate' | 'pricePerKg' | 'lastOrderCode'>,
): Promise<string> => {
  const ref = doc(collection(db, 'supplyPlans'));
  await setDoc(ref, {
    ...stripUndefined(data as Record<string, unknown>),
    status: 'pending',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return ref.id;
};

export const getSupplyPlans = async (): Promise<SupplyPlan[]> => {
  const snap = await getDocs(query(collection(db, 'supplyPlans'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => mapPlan(d.id, d.data()));
};

export const updateSupplyPlan = async (id: string, updates: Partial<SupplyPlan>): Promise<void> => {
  const data: any = { ...stripUndefined(updates as Record<string, unknown>), updatedAt: Timestamp.now() };
  if (updates.nextDeliveryDate) data.nextDeliveryDate = Timestamp.fromDate(new Date(updates.nextDeliveryDate));
  await updateDoc(doc(db, 'supplyPlans', id), data);
};

/** Generate a customer order from a plan and advance its next delivery date. */
export const generateOrderFromPlan = async (plan: SupplyPlan): Promise<string> => {
  const rate = plan.pricePerKg || 0;
  const items: OrderItem[] = plan.items.map(i => ({
    onionType: i.onionType,
    onionSize: i.onionSize,
    quantityKg: i.quantityKg,
    pricePerKg: rate || undefined,
    subtotal: rate ? rate * i.quantityKg : undefined,
  }));
  const totalWeightKg = plan.items.reduce((s, i) => s + (i.quantityKg || 0), 0);
  const estimatedTotal = rate ? rate * totalWeightKg : undefined;

  const code = await createCustomerOrder({
    customerName: plan.businessName,
    customerPhone: plan.phone,
    customerEmail: plan.email || undefined,
    deliveryLocation: plan.deliveryLocation,
    houseNumber: '—',
    estate: plan.estate || plan.businessName,
    deliveryInstructions: `Standing supply order · ${plan.frequency}`,
    items,
    totalWeightKg,
    estimatedTotal,
    pricePerKgSnapshot: rate || undefined,
    deliveryFee: 0,
    paymentMethod: 'pay_on_delivery',
  } as any);

  const days = SUPPLY_FREQUENCY_DAYS[plan.frequency];
  const next = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  await updateSupplyPlan(plan.id, { nextDeliveryDate: next, lastOrderCode: code });
  return code;
};

// ── Genvitungu Plus memberships ───────────────────────────────────────────────
const mapMembership = (id: string, d: any): Membership => ({
  id,
  ...d,
  startedAt: d.startedAt ? timestampToDate(d.startedAt) : undefined,
  expiresAt: d.expiresAt ? timestampToDate(d.expiresAt) : undefined,
  createdAt: timestampToDate(d.createdAt),
  updatedAt: timestampToDate(d.updatedAt),
});

/** Look up a membership by its phone key (see lib/membership.memberKey). */
export const getMembershipByPhone = async (key: string): Promise<Membership | null> => {
  if (!key) return null;
  const snap = await getDoc(doc(db, 'memberships', key));
  return snap.exists() ? mapMembership(snap.id, snap.data()) : null;
};

/** Create a pending join request (consumer). Idempotent — leaves an existing
 *  membership untouched (consumers can't update, only create). */
export const createMembershipRequest = async (
  key: string,
  data: { phone: string; name: string; mpesaCode?: string },
): Promise<void> => {
  const ref = doc(db, 'memberships', key);
  const existing = await getDoc(ref);
  if (existing.exists()) return; // already a member/pending — payment intent will still fire
  await setDoc(ref, {
    ...data,
    plan: 'plus',
    status: 'pending',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
};

export const getMemberships = async (): Promise<Membership[]> => {
  const snap = await getDocs(query(collection(db, 'memberships'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => mapMembership(d.id, d.data()));
};

export const updateMembership = async (key: string, updates: Partial<Membership>): Promise<void> => {
  const data: any = { ...updates, updatedAt: Timestamp.now() };
  if (updates.startedAt) data.startedAt = Timestamp.fromDate(new Date(updates.startedAt));
  if (updates.expiresAt) data.expiresAt = Timestamp.fromDate(new Date(updates.expiresAt));
  await updateDoc(doc(db, 'memberships', key), data);
};

// ── Post-delivery quality claims ──────────────────────────────────────────────
export const createQualityClaim = async (
  c: Omit<QualityClaim, 'id' | 'status' | 'createdAt' | 'updatedAt'>,
): Promise<string> => {
  const ref = doc(collection(db, 'qualityClaims'));
  await setDoc(ref, {
    ...c,
    status: 'pending',
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return ref.id;
};

const mapClaim = (id: string, d: any): QualityClaim => ({
  id,
  ...d,
  createdAt: timestampToDate(d.createdAt),
  updatedAt: timestampToDate(d.updatedAt),
  resolvedAt: d.resolvedAt ? timestampToDate(d.resolvedAt) : undefined,
});

export const getQualityClaimByTrackingCode = async (code: string): Promise<QualityClaim | null> => {
  const snap = await getDocs(query(collection(db, 'qualityClaims'), where('trackingCode', '==', code), limit(1)));
  if (snap.empty) return null;
  return mapClaim(snap.docs[0].id, snap.docs[0].data());
};

export const getQualityClaims = async (): Promise<QualityClaim[]> => {
  const snap = await getDocs(query(collection(db, 'qualityClaims'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => mapClaim(d.id, d.data()));
};

export const updateQualityClaim = async (id: string, updates: Partial<QualityClaim>): Promise<void> => {
  const data: any = { ...updates, updatedAt: Timestamp.now() };
  if (updates.resolvedAt) data.resolvedAt = Timestamp.fromDate(new Date(updates.resolvedAt));
  await updateDoc(doc(db, 'qualityClaims', id), data);
};

export const getAllClients = async (): Promise<Client[]> => {
  const clients = await queryDocuments<Client>('clients', [
    orderBy('lastPurchaseDate', 'desc'),
  ]);
  
  return clients.map(client => ({
    ...client,
    lastPurchaseDate: timestampToDate(client.lastPurchaseDate),
    firstPurchaseDate: timestampToDate(client.firstPurchaseDate),
    secondLastPurchaseDate: client.secondLastPurchaseDate 
      ? timestampToDate(client.secondLastPurchaseDate)
      : undefined,
    predictedNextPurchase: client.predictedNextPurchase
      ? timestampToDate(client.predictedNextPurchase)
      : undefined,
    lastUpdated: timestampToDate(client.lastUpdated),
    createdAt: timestampToDate(client.createdAt),
  }));
};

export const getTopClients = async (limitCount: number = 20): Promise<Client[]> => {
  const clients = await queryDocuments<Client>('clients', [
    orderBy('totalPurchaseValue', 'desc'),
    limit(limitCount),
  ]);
  
  return clients.map(client => ({
    ...client,
    lastPurchaseDate: timestampToDate(client.lastPurchaseDate),
    firstPurchaseDate: timestampToDate(client.firstPurchaseDate),
    secondLastPurchaseDate: client.secondLastPurchaseDate 
      ? timestampToDate(client.secondLastPurchaseDate)
      : undefined,
    predictedNextPurchase: client.predictedNextPurchase
      ? timestampToDate(client.predictedNextPurchase)
      : undefined,
    lastUpdated: timestampToDate(client.lastUpdated),
    createdAt: timestampToDate(client.createdAt),
  }));
};

export const getClientsLikelyToBuy = async (days: number = 3): Promise<Client[]> => {
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + days);
  
  const clients = await queryDocuments<Client>('clients', [
    where('predictedNextPurchase', '<=', Timestamp.fromDate(futureDate)),
    orderBy('predictedNextPurchase', 'asc'),
  ]);
  
  return clients.map(client => ({
    ...client,
    lastPurchaseDate: timestampToDate(client.lastPurchaseDate),
    firstPurchaseDate: timestampToDate(client.firstPurchaseDate),
    secondLastPurchaseDate: client.secondLastPurchaseDate 
      ? timestampToDate(client.secondLastPurchaseDate)
      : undefined,
    predictedNextPurchase: client.predictedNextPurchase
      ? timestampToDate(client.predictedNextPurchase)
      : undefined,
    lastUpdated: timestampToDate(client.lastUpdated),
    createdAt: timestampToDate(client.createdAt),
  }));
};

// Expense operations
export const createExpense = async (expense: Omit<Expense, 'id'>): Promise<string> => {
  const expensesRef = collection(db, 'expenses');
  const docRef = doc(expensesRef);
  await setDoc(docRef, {
    ...expense,
    createdAt: Timestamp.now(),
    date: Timestamp.fromDate(new Date(expense.date)),
  });
  return docRef.id;
};

export const getExpensesByDateRange = async (start: Date, end: Date): Promise<Expense[]> => {
  const expenses = await queryDocuments<Expense>('expenses', [
    where('date', '>=', Timestamp.fromDate(start)),
    where('date', '<=', Timestamp.fromDate(end)),
    orderBy('date', 'desc'),
  ]);
  
  return expenses.map(expense => ({
    ...expense,
    date: timestampToDate(expense.date),
    createdAt: timestampToDate(expense.createdAt),
  }));
};

export const getAllExpenses = async (): Promise<Expense[]> => {
  const expenses = await queryDocuments<Expense>('expenses', [
    orderBy('date', 'desc'),
  ]);
  
  return expenses.map(expense => ({
    ...expense,
    date: timestampToDate(expense.date),
    createdAt: timestampToDate(expense.createdAt),
  }));
};

// Inventory operations
export const createInventoryBatch = async (batch: Omit<InventoryBatch, 'id'>): Promise<string> => {
  const inventoryRef = collection(db, 'inventory');
  const docRef = doc(inventoryRef);
  await setDoc(docRef, {
    ...batch,
    createdAt: Timestamp.now(),
    batchDate: Timestamp.fromDate(new Date(batch.batchDate)),
  });
  return docRef.id;
};

export const updateInventoryBatch = async (
  batchId: string,
  updates: Partial<InventoryBatch>
): Promise<void> => {
  await updateDocument<InventoryBatch>('inventory', batchId, updates);
};

export const getAllInventoryBatches = async (): Promise<InventoryBatch[]> => {
  const batches = await queryDocuments<InventoryBatch>('inventory', [
    orderBy('batchDate', 'desc'),
  ]);
  
  return batches.map(batch => ({
    ...batch,
    batchDate: timestampToDate(batch.batchDate),
    createdAt: timestampToDate(batch.createdAt),
  }));
};

export const getActiveInventoryBatches = async (): Promise<InventoryBatch[]> => {
  const batches = await queryDocuments<InventoryBatch>('inventory', [
    where('currentStockKg', '>', 0),
    orderBy('batchDate', 'desc'),
  ]);
  
  return batches.map(batch => ({
    ...batch,
    batchDate: timestampToDate(batch.batchDate),
    createdAt: timestampToDate(batch.createdAt),
  }));
};

// Stock movement operations
export const createStockMovement = async (movement: Omit<StockMovement, 'id'>): Promise<string> => {
  const movementsRef = collection(db, 'stockMovements');
  const docRef = doc(movementsRef);
  await setDoc(docRef, {
    ...movement,
    createdAt: Timestamp.now(),
    date: Timestamp.fromDate(new Date(movement.date)),
  });
  return docRef.id;
};

// Call log operations
export const createCallLog = async (log: Omit<CallLog, 'id'>): Promise<string> => {
  const callLogsRef = collection(db, 'callLogs');
  const docRef = doc(callLogsRef);
  await setDoc(docRef, {
    ...log,
    date: Timestamp.now(),
    followUpDate: log.followUpDate ? Timestamp.fromDate(new Date(log.followUpDate)) : null,
  });
  return docRef.id;
};

export const getCallLogsByClient = async (clientId: string): Promise<CallLog[]> => {
  const logs = await queryDocuments<CallLog>('callLogs', [
    where('clientId', '==', clientId),
    orderBy('date', 'desc'),
  ]);
  
  return logs.map(log => ({
    ...log,
    date: timestampToDate(log.date),
    followUpDate: log.followUpDate ? timestampToDate(log.followUpDate) : undefined,
  }));
};

// User operations
export const createUser = async (user: User): Promise<void> => {
  await createDocument<User>('users', user.id, user);
};

export const getUser = async (userId: string): Promise<User | null> => {
  return getDocument<User>('users', userId);
};

// Real-time subscriptions
export const subscribeToDashboard = (
  callback: (data: any) => void
): (() => void) => {
  const unsubscribers: (() => void)[] = [];
  
  // Subscribe to sales
  const salesRef = collection(db, 'sales');
  const salesQuery = query(salesRef, orderBy('saleDate', 'desc'), limit(100));
  unsubscribers.push(onSnapshot(salesQuery, (snapshot) => {
    callback({ type: 'sales', data: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) });
  }));
  
  // Subscribe to clients
  const clientsRef = collection(db, 'clients');
  unsubscribers.push(onSnapshot(clientsRef, (snapshot) => {
    callback({ type: 'clients', data: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) });
  }));
  
  return () => {
    unsubscribers.forEach(unsub => unsub());
  };
};

// ====== MULTI-STORE ERP OPERATIONS (v3.0) ======

// Store Management
export const createStore = async (store: Omit<Store, 'id'>): Promise<string> => {
  const storesRef = collection(db, 'stores');
  const docRef = doc(storesRef);
  await setDoc(docRef, {
    ...store,
    createdAt: Timestamp.fromDate(new Date(store.createdAt)),
  });
  return docRef.id;
};

export const getStore = async (storeId: string): Promise<Store | null> => {
  const store = await getDocument<Store>('stores', storeId);
  if (!store) return null;
  
  return {
    ...store,
    createdAt: timestampToDate(store.createdAt),
  };
};

export const getAllStores = async (): Promise<Store[]> => {
  const stores = await queryDocuments<Store>('stores', [
    orderBy('name', 'asc'),
  ]);
  
  return stores.map(store => ({
    ...store,
    createdAt: timestampToDate(store.createdAt),
  }));
};

export const getActiveStores = async (): Promise<Store[]> => {
  const stores = await queryDocuments<Store>('stores', [
    where('status', '==', 'active'),
    orderBy('name', 'asc'),
  ]);
  
  return stores.map(store => ({
    ...store,
    createdAt: timestampToDate(store.createdAt),
  }));
};

export const updateStore = async (
  storeId: string,
  updates: Partial<Store>
): Promise<void> => {
  await updateDocument<Store>('stores', storeId, updates);
};

// Store Request Operations
export const createStoreRequest = async (request: Omit<StoreRequest, 'id'>): Promise<string> => {
  const requestsRef = collection(db, 'storeRequests');
  const docRef = doc(requestsRef);
  await setDoc(docRef, {
    ...request,
    createdAt: Timestamp.fromDate(new Date(request.createdAt)),
    updatedAt: Timestamp.fromDate(new Date(request.updatedAt)),
  });
  return docRef.id;
};

export const getStoreRequests = async (storeId: string): Promise<StoreRequest[]> => {
  const requests = await queryDocuments<StoreRequest>('storeRequests', [
    where('storeId', '==', storeId),
    orderBy('createdAt', 'desc'),
  ]);
  
  return requests.map(request => ({
    ...request,
    createdAt: timestampToDate(request.createdAt),
    updatedAt: timestampToDate(request.updatedAt),
  }));
};

export const getAllStoreRequests = async (): Promise<StoreRequest[]> => {
  const requests = await queryDocuments<StoreRequest>('storeRequests', [
    orderBy('createdAt', 'desc'),
  ]);
  
  return requests.map(request => ({
    ...request,
    createdAt: timestampToDate(request.createdAt),
    updatedAt: timestampToDate(request.updatedAt),
  }));
};

export const updateStoreRequest = async (
  requestId: string,
  updates: Partial<StoreRequest>
): Promise<void> => {
  const updateData = {
    ...updates,
    updatedAt: Timestamp.now().toDate(),
  };
  await updateDocument<StoreRequest>('storeRequests', requestId, updateData);
};

// Sales by Store
export const getSalesByStore = async (storeId: string): Promise<Sale[]> => {
  const sales = await queryDocuments<Sale>('sales', [
    where('storeId', '==', storeId),
    orderBy('saleDate', 'desc'),
  ]);
  
  return sales.map(sale => ({
    ...sale,
    saleDate: timestampToDate(sale.saleDate),
    createdAt: timestampToDate(sale.createdAt),
  }));
};

// Inventory by Store
export const getInventoryByStore = async (storeId: string): Promise<InventoryBatch[]> => {
  const batches = await queryDocuments<InventoryBatch>('inventory', [
    where('storeId', '==', storeId),
    orderBy('batchDate', 'desc'),
  ]);
  
  return batches.map(batch => ({
    ...batch,
    batchDate: timestampToDate(batch.batchDate),
    createdAt: timestampToDate(batch.createdAt),
  }));
};

// Inventory Transfer Operations
export const createInventoryTransfer = async (transfer: Omit<InventoryTransfer, 'id'>): Promise<string> => {
  const transfersRef = collection(db, 'inventoryTransfers');
  const docRef = doc(transfersRef);
  await setDoc(docRef, {
    ...transfer,
    requestDate: Timestamp.fromDate(new Date(transfer.requestDate)),
    completionDate: transfer.completionDate ? Timestamp.fromDate(new Date(transfer.completionDate)) : null,
  });
  return docRef.id;
};

export const getInventoryTransfers = async (): Promise<InventoryTransfer[]> => {
  const transfers = await queryDocuments<InventoryTransfer>('inventoryTransfers', [
    orderBy('requestDate', 'desc'),
  ]);
  
  return transfers.map(transfer => ({
    ...transfer,
    requestDate: timestampToDate(transfer.requestDate),
    completionDate: transfer.completionDate ? timestampToDate(transfer.completionDate) : undefined,
  }));
};

export const getPendingInventoryTransfers = async (): Promise<InventoryTransfer[]> => {
  const transfers = await queryDocuments<InventoryTransfer>('inventoryTransfers', [
    where('status', '==', 'pending_approval'),
    orderBy('requestDate', 'asc'),
  ]);
  
  return transfers.map(transfer => ({
    ...transfer,
    requestDate: timestampToDate(transfer.requestDate),
    completionDate: transfer.completionDate ? timestampToDate(transfer.completionDate) : undefined,
  }));
};

export const updateInventoryTransfer = async (
  transferId: string,
  updates: Partial<InventoryTransfer>
): Promise<void> => {
  const updateData: any = { ...updates };
  if (updates.completionDate) {
    updateData.completionDate = Timestamp.fromDate(new Date(updates.completionDate));
  }
  await updateDocument<InventoryTransfer>('inventoryTransfers', transferId, updateData);
};

// Store Audit Operations
export const createStoreAudit = async (audit: Omit<StoreAudit, 'id'>): Promise<string> => {
  const auditsRef = collection(db, 'storeAudits');
  const docRef = doc(auditsRef);
  await setDoc(docRef, {
    ...audit,
    date: Timestamp.fromDate(new Date(audit.date)),
    createdAt: Timestamp.fromDate(new Date(audit.createdAt)),
  });
  return docRef.id;
};

export const getStoreAudits = async (storeId: string): Promise<StoreAudit[]> => {
  const audits = await queryDocuments<StoreAudit>('storeAudits', [
    where('storeId', '==', storeId),
    orderBy('date', 'desc'),
  ]);
  
  return audits.map(audit => ({
    ...audit,
    date: timestampToDate(audit.date),
    createdAt: timestampToDate(audit.createdAt),
  }));
};

export const getAllStoreAudits = async (): Promise<StoreAudit[]> => {
  const audits = await queryDocuments<StoreAudit>('storeAudits', [
    orderBy('date', 'desc'),
  ]);
  
  return audits.map(audit => ({
    ...audit,
    date: timestampToDate(audit.date),
    createdAt: timestampToDate(audit.createdAt),
  }));
};

// Get average audit score for a store
export const getStoreAverageAuditScore = async (storeId: string): Promise<number> => {
  const audits = await getStoreAudits(storeId);
  if (audits.length === 0) return 0;
  
  const totalScore = audits.reduce((sum, audit) => sum + audit.overallScore, 0);
  return totalScore / audits.length;
};

// Get sales with location for heatmap
export const getSalesWithLocation = async (): Promise<Sale[]> => {
  const allSales = await getAllSales();
  return allSales.filter(sale => sale.location && sale.location.lat && sale.location.lng);
};

// ====== AUDIT TRAIL (v4.0) ======

export const logAuditChange = async (params: {
  collection: AuditCollection;
  documentId: string;
  documentLabel: string;
  action: AuditAction;
  changedBy: string;
  changedByName: string;
  changedByRole: UserRole;
  storeId?: string;
  before: Record<string, any>;
  after: Record<string, any>;
  reason?: string;
}): Promise<void> => {
  const changedFields = Object.keys(params.after).filter(
    key => JSON.stringify(params.before[key]) !== JSON.stringify(params.after[key])
  );
  const docData: Record<string, any> = { ...params, changedFields, createdAt: Timestamp.now() };
  // Firestore rejects undefined field values — remove them before writing
  Object.keys(docData).forEach(key => { if (docData[key] === undefined) delete docData[key]; });
  await addDoc(collection(db, 'auditLogs'), docData);
};

export const getAuditLogs = async (options?: {
  collectionName?: AuditCollection;
  documentId?: string;
  storeId?: string;
  limitCount?: number;
}): Promise<AuditLogEntry[]> => {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
  if (options?.collectionName) constraints.push(where('collection', '==', options.collectionName));
  if (options?.documentId) constraints.push(where('documentId', '==', options.documentId));
  if (options?.storeId) constraints.push(where('storeId', '==', options.storeId));
  if (options?.limitCount) constraints.push(limit(options.limitCount));

  const q = query(collection(db, 'auditLogs'), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({
    id: d.id,
    ...d.data(),
    createdAt: timestampToDate(d.data().createdAt),
  } as AuditLogEntry));
};

// ====== CUSTOMER ORDERS (v4.0) ======

// High-entropy, non-guessable tracking code (~96 bits). Web Crypto in the browser;
// the consumer order path mints its code server-side (createOrder) — this is used
// by the staff-side supply-order generator.
const generateTrackingCode = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  let bin = '';
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  const b64url = btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `GV-${b64url}`;
};

/** Remove undefined values so Firestore doesn't reject the write. */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k in obj) {
    if (obj[k] !== undefined && obj[k] !== '') out[k] = obj[k];
  }
  return out;
}

export const createCustomerOrder = async (
  order: Omit<CustomerOrder, 'id' | 'trackingCode' | 'status' | 'paymentStatus' | 'createdAt' | 'updatedAt' | 'emailsSent'>
): Promise<string> => {
  // Up to 3 attempts to avoid the (rare) tracking-code collision.
  for (let attempt = 0; attempt < 3; attempt++) {
    const trackingCode = generateTrackingCode();
    const existing = await getDocs(
      query(collection(db, 'customerOrders'), where('trackingCode', '==', trackingCode), limit(1))
    );
    if (!existing.empty) continue;

    await addDoc(collection(db, 'customerOrders'), {
      ...stripUndefined(order as Record<string, unknown>),
      trackingCode,
      status: 'pending',
      paymentStatus: 'pending',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return trackingCode;
  }
  throw new Error('Could not generate a unique tracking code. Please try again.');
};

// Consumer order placement — the ONLY consumer write path. The server (createOrder
// callable) recomputes every money field from settings/pricing; the client cannot
// dictate the amount any more (F-02). Returns the server-authored totals.
export interface PlaceOrderInput {
  customerName: string;
  phone: string;
  customerAltPhone?: string;
  customerEmail?: string;
  deliveryArea: string;
  houseNumber: string;
  estate: string;
  deliveryInstructions?: string;
  items: { onionType: string; onionSize: string; quantityKg: number }[];
  paymentMethod: OrderPaymentMethod;
  mpesaConfirmationCode?: string;
}
export interface PlacedOrder {
  trackingCode: string;
  estimatedTotal: number;
  deliveryFee: number;
  discount: number;
  total: number;
  totalWeightKg: number;
}
export const placeConsumerOrder = async (input: PlaceOrderInput): Promise<PlacedOrder> => {
  const call = httpsCallable<PlaceOrderInput, PlacedOrder>(functions, 'createOrder');
  const res = await call(input);
  return res.data as PlacedOrder;
};

export const getCustomerOrders = async (options?: {
  status?: OrderStatus;
  storeId?: string;
  limitCount?: number;
}): Promise<CustomerOrder[]> => {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
  if (options?.status) constraints.push(where('status', '==', options.status));
  if (options?.storeId) constraints.push(where('assignedStoreId', '==', options.storeId));
  if (options?.limitCount) constraints.push(limit(options.limitCount));

  const q = query(collection(db, 'customerOrders'), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({
    id: d.id,
    ...d.data(),
    createdAt: timestampToDate(d.data().createdAt),
    updatedAt: timestampToDate(d.data().updatedAt),
    confirmedAt: d.data().confirmedAt ? timestampToDate(d.data().confirmedAt) : undefined,
    deliveredAt: d.data().deliveredAt ? timestampToDate(d.data().deliveredAt) : undefined,
  } as CustomerOrder));
};

// What the public order tracker receives — a redacted projection from the
// trackOrder callable (no email, alt phone, invoice ids, notification audit).
export type TrackedOrder = Pick<CustomerOrder,
  'id' | 'trackingCode' | 'customerName' | 'customerPhone' | 'deliveryLocation' |
  'houseNumber' | 'estate' | 'items' | 'totalWeightKg' | 'estimatedTotal' |
  'deliveryFee' | 'paymentMethod' | 'paymentStatus' | 'status' | 'assignedStoreName' | 'adminNotes'>;

/**
 * Look up an order for the public tracker. Requires BOTH the tracking code and
 * the phone number on the order (two-factor) — customerOrders is no longer
 * readable from the client, so this goes through the trackOrder Cloud Function.
 * Returns null when the pair doesn't match a real order.
 */
export const trackOrderByCode = async (
  code: string,
  phone: string,
): Promise<TrackedOrder | null> => {
  try {
    const call = httpsCallable<{ code: string; phone: string }, TrackedOrder>(functions, 'trackOrder');
    const res = await call({ code: code.trim(), phone: phone.trim() });
    return res.data as TrackedOrder;
  } catch (e: any) {
    if (e?.code === 'functions/not-found') return null; // wrong code/phone pair
    throw e;
  }
};

export const updateCustomerOrder = async (
  orderId: string,
  updates: Partial<CustomerOrder>
): Promise<void> => {
  await updateDoc(doc(db, 'customerOrders', orderId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
};
